import * as React from "react";
import { SlideKey } from "../config/slides";

/**
 * Qalqi Logo component
 * @param {QalqiLogoProps} props - SVG properties + active slide
 * @returns {JSX.Element} The rendered logo
 */
const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    let h = 0;
    if (delta !== 0) {
        if (max === r) h = ((g - b) / delta) % 6;
        else if (max === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
        h = Math.round(h * 60);
        if (h < 0) h += 360;
    }
    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    return { h, s: Math.round(s * 100), l: Math.round(l * 100) };
};

const hsl = ({ h, s, l }: { h: number; s: number; l: number }) =>
    `hsl(${h} ${s}% ${l}%)`;

const APP_COLOR_HUE = hexToHsl("#4707CE").h;

const dark = hexToHsl("#464344");
const red1 = hexToHsl("#C82128");
const red2 = hexToHsl("#811418");

interface QalqiLogoProps extends React.SVGProps<SVGSVGElement> {
    activeSlide?: SlideKey;
}

const QalqiLogo = ({ activeSlide, ...props }: QalqiLogoProps) => {
    const onAppSlide = activeSlide === "1234";
    const shiftedRed1 = onAppSlide
        ? { ...red1, h: APP_COLOR_HUE }
        : red1;
    const shiftedRed2 = onAppSlide
        ? { ...red2, h: APP_COLOR_HUE }
        : red2;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={83}
            height={18}
            fill="none"
            {...props}
        >
            <path
                fill={hsl(dark)}
                d="M8.75 17.583c-1.458 0-2.732-.246-3.822-.737-1.09-.506-2.004-1.174-2.74-2.003A8.866 8.866 0 0 1 .552 12.01 9.55 9.55 0 0 1 0 8.81c0-1.09.184-2.15.553-3.178a8.599 8.599 0 0 1 1.635-2.809c.736-.829 1.65-1.489 2.74-1.98C6.018.336 7.292.083 8.75.083c1.443 0 2.71.253 3.8.76 1.09.491 1.995 1.151 2.716 1.98a8.394 8.394 0 0 1 1.658 2.81 9.352 9.352 0 0 1 .553 3.177c0 1.06-.177 2.103-.53 3.132a8.42 8.42 0 0 1-1.52 2.74l2.326 2.67h-4.098l-.645-.736a8.632 8.632 0 0 1-1.98.714 9.782 9.782 0 0 1-2.28.253Zm0-3.57c.599 0 1.159-.099 1.68-.298l-2.578-2.902 2.487-2.003 2.371 2.74c.415-.829.622-1.742.622-2.74 0-.86-.176-1.68-.53-2.464A4.607 4.607 0 0 0 11.26 4.39c-.676-.507-1.512-.76-2.51-.76-1.013 0-1.865.253-2.556.76a4.676 4.676 0 0 0-1.52 1.957 6.156 6.156 0 0 0-.506 2.464c0 .875.169 1.712.506 2.51a4.705 4.705 0 0 0 1.52 1.934c.69.507 1.543.76 2.556.76ZM18.838 17.353 24.572.313h5.342l5.734 17.04H31.48l-.99-3.109h-6.494l-.99 3.109h-4.168Zm6.264-6.517h4.305l-2.164-6.7-2.141 6.7ZM37.703 17.353V.313h3.984V13.9h8.197v3.454h-12.18Z"
            />
            <path
                fill={hsl(shiftedRed1)}
                d="M59.885 17.583c-1.459 0-2.733-.246-3.823-.737-1.09-.506-2.003-1.174-2.74-2.003a8.867 8.867 0 0 1-1.635-2.832 9.55 9.55 0 0 1-.552-3.201c0-1.09.184-2.15.552-3.178a8.6 8.6 0 0 1 1.635-2.809c.737-.829 1.65-1.489 2.74-1.98 1.09-.507 2.364-.76 3.823-.76 1.443 0 2.709.253 3.799.76 1.09.491 1.996 1.151 2.717 1.98a8.394 8.394 0 0 1 1.658 2.81 9.352 9.352 0 0 1 .553 3.177c0 1.06-.177 2.103-.53 3.132a8.42 8.42 0 0 1-1.52 2.74l2.326 2.67h-4.099l-.645-.736a8.632 8.632 0 0 1-1.98.714 9.782 9.782 0 0 1-2.28.253Zm0-3.57c.598 0 1.159-.099 1.68-.298l-2.578-2.902 2.486-2.003 2.372 2.74c.415-.829.622-1.742.622-2.74 0-.86-.177-1.68-.53-2.464a4.607 4.607 0 0 0-1.542-1.957c-.676-.507-1.513-.76-2.51-.76-1.014 0-1.865.253-2.556.76a4.677 4.677 0 0 0-1.52 1.957 6.156 6.156 0 0 0-.507 2.464c0 .875.17 1.712.507 2.51a4.706 4.706 0 0 0 1.52 1.934c.69.507 1.542.76 2.556.76Z"
            />
            <path
                fill={hsl(dark)}
                d="M70.695.32h11.746v3.456H78.58v10.098h3.862v3.455H78.58v.031h-3.984v-.031h-3.9v-3.455h3.9V3.776h-3.9V.32Z"
            />
            <path
                fill={hsl(shiftedRed2)}
                d="m61.47 8.81-2.497 1.997 5.816 6.546h4.099L61.469 8.81Z"
            />
        </svg>
    );
};
export default QalqiLogo;
